import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  FileText,
  Eye,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ShoppingBag,
  DollarSign,
  Sparkles,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, isValid, parseISO } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import purchasesService from "../../../api/PurchasesService";
import { toast } from "react-toastify";
import Loader from "../../../components/misc/Loader";

const formatPurchaseDateTime = (value) => {
  if (!value) return null;
  const normalized = String(value).replace(/(\.\d{3})\d+/, "$1");
  const parsed = parseISO(normalized);
  if (!isValid(parsed)) return null;
  return {
    date: format(parsed, "MMM dd, yyyy"),
    time: format(parsed, "h:mm a"),
  };
};

const EmptyState = ({ icon: Icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="flex flex-col items-center justify-center py-16"
  >
    <div className="mb-4 p-4 bg-blue-100 rounded-full">
      <Icon className="w-12 h-12 text-blue-400" />
    </div>
    <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
    <p className="text-center text-gray-500 max-w-md">{description}</p>
  </motion.div>
);

const StatusBadge = ({ paymentStatus }) => {
  // payment_status: 0 = pending, 1 = completed
  const status = paymentStatus === 1 ? "completed" : "pending";
  
  const configs = {
    completed: {
      icon: CheckCircle,
      color: "bg-green-100 text-green-800 border-green-200",
      label: "Completed",
    },
    pending: {
      icon: Clock,
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      label: "Pending",
    },
    failed: {
      icon: XCircle,
      color: "bg-red-100 text-red-800 border-red-200",
      label: "Failed",
    },
  };

  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <motion.span
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}
    >
      <Icon className="mr-1 w-3 h-3" />
      {config.label}
    </motion.span>
  );
};

export default function PurchasesManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [purchasesData, setPurchasesData] = useState([]);
  const [loading, setLoading] = useState(true);
  // Modal functionality can be added later when needed
  // const [selectedPurchase, setSelectedPurchase] = useState(null);
  // const [showDetailsModal, setShowDetailsModal] = useState(false);
  const pageSize = 10;

  // Check if we're filtering by a specific policy
  const policyContext = location.state?.filterByPolicy 
    ? {
        policySlug: location.state.policySlug,
        policyName: location.state.policyName,
        from: location.state.from
      }
    : null;

  useEffect(() => {
    const fetchPurchases = async () => {
      setLoading(true);
      try {
        let response;
        
        // If filtering by policy, use the policy-specific endpoint
        if (policyContext?.policySlug) {
          response = await purchasesService.getPurchasesByPolicy(policyContext.policySlug);
        } else {
          // Otherwise, fetch all purchases
          response = await purchasesService.getAllPurchases();
        }
        
        if (response.data?.status_code === 200 && response.data.data) {
          const data = response.data.data;
          
          // Handle array or object with numeric keys
          let purchasesArray = [];
          if (Array.isArray(data)) {
            purchasesArray = data;
          } else if (typeof data === 'object' && !Array.isArray(data)) {
            purchasesArray = Object.values(data);
          }
          
          console.log("Fetched purchases:", purchasesArray.length, "items");
          setPurchasesData(purchasesArray);
        } else {
          toast.error(response.data?.reason || response.message || "Failed to fetch purchases");
          setPurchasesData([]);
        }
      } catch (error) {
        toast.error("An error occurred while getting purchases");
        setPurchasesData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPurchases();
  }, [policyContext?.policySlug]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalPurchases = purchasesData.length;
    const totalAmount = purchasesData.reduce((sum, purchase) => {
      const purchasedData = purchase.purchased_policy_data || {};
      const policy = purchase.policy || {};
      let amount = 0;
      
      if (policy.policy_type === "intercity_travel") {
        // For intercity travel: use amount or coverageDuration.price
        amount = parseFloat(
          purchasedData.amount || 
          purchasedData.coverageDuration?.price || 
          purchase.amount || 
          0
        );
      } else {
        // For other policy types: use premium or amount
        amount = parseFloat(
          purchasedData.premium || 
          purchasedData.amount || 
          purchase.amount || 
          0
        );
      }
      
      return sum + amount;
    }, 0);
    const uniquePolicies = new Set(
      purchasesData
        .map((p) => p.insurance_policy_slug || p.policy?.slug)
        .filter(Boolean)
    ).size;
    const completedPurchases = purchasesData.filter((p) => p.payment_status === 1).length;

    return {
      totalPurchases,
      totalAmount,
      uniquePolicies,
      completedPurchases,
    };
  }, [purchasesData]);

  const filteredPurchases = useMemo(() => {
    return purchasesData.filter((purchase) => {
      const user = purchase.user || {};
      const policy = purchase.policy || purchase.insurance_policy || {};
      
      const purchasedData = purchase.purchased_policy_data || {};
      const matchesGlobal =
        globalFilter === "" ||
        purchase.slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchase.transaction_id?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchase.policy_slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        policy.policy_name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        policy.policy_type?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        user.email?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        user.first_name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchase.user_slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchasedData.product_name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchasedData.departureCity?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchasedData.destinationCity?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchasedData.departureRegion?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchasedData.destinationRegion?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchasedData.routeDestination?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchasedData.vehicleType?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        purchasedData.busLicensePlate?.toLowerCase().includes(globalFilter.toLowerCase());

      const purchaseStatus = purchase.status || purchase.payment_status || "completed";
      const matchesStatus =
        statusFilter === "all" ||
        purchaseStatus.toLowerCase() === statusFilter.toLowerCase();

      const purchaseDate = purchase.created_at || purchase.purchase_date || purchase.date;
      const matchesDateRange =
        !startDate ||
        !endDate ||
        (purchaseDate &&
          parseISO(purchaseDate) >= startDate &&
          parseISO(purchaseDate) <= endDate);

      return matchesGlobal && matchesStatus && matchesDateRange;
    });
  }, [purchasesData, globalFilter, statusFilter, startDate, endDate]);

  const totalPages = Math.ceil(filteredPurchases.length / pageSize);
  const paginatedPurchases = filteredPurchases.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return <Loader fullScreen={true} text="Loading purchases..." />;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="mx-auto max-w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              {policyContext ? (
                <div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (policyContext.from) {
                        navigate(policyContext.from);
                      } else {
                        navigate('/admin/insurance-policies');
                      }
                    }}
                    className="flex items-center mb-4 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back to Policies
                  </motion.button>
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg">
                      <ShoppingBag className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                        Purchases for {policyContext.policyName || "Policy"}
                      </h1>
                      <p className="text-gray-600 mt-1">
                        View all purchases for this specific policy
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3 mb-2">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                    <ShoppingBag className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      Purchases Management
                    </h1>
                    <p className="text-gray-600 mt-1">
                      View and manage all policy purchases
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Stats Cards - Only show when not filtering by policy */}
        {!policyContext && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4"
          >
          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 shadow-lg"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                <ShoppingBag className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Purchases</p>
                <p className="text-3xl font-bold text-blue-700">{stats.totalPurchases}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 shadow-lg"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-3xl font-bold text-blue-700">₵{stats.totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 shadow-lg"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Policies</p>
                <p className="text-3xl font-bold text-blue-700">{stats.uniquePolicies}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 shadow-lg"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-3xl font-bold text-blue-700">{stats.completedPurchases}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
        )}

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6 bg-white rounded-xl border-2 border-blue-200 shadow-lg"
        >
          <div className="p-6">
            <div className="flex flex-col mb-4 lg:flex-row lg:items-center lg:justify-between">
              <h3 className="mb-4 text-lg font-semibold text-gray-900 lg:mb-0 flex items-center">
                <Sparkles className="mr-2 w-5 h-5 text-blue-600" />
                Search & Filters
              </h3>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 space-x-2 text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 lg:hidden"
              >
                <Filter className="w-4 h-4" />
                <span>Toggle Filters</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    showFilters ? "rotate-180" : ""
                  }`}
                />
              </motion.button>
            </div>

            <AnimatePresence>
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className={`${showFilters ? "block" : "hidden"} lg:block`}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search purchases..."
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      className="py-2 pr-4 pl-10 w-full rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>

                  <div className="col-span-1">
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => setDateRange(update)}
                      placeholderText="Select date range"
                      className="px-4 py-2 w-full rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Purchases Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="overflow-hidden bg-white rounded-xl border-2 border-blue-200 shadow-lg"
        >
          {filteredPurchases.length === 0 ? (
            <div className="py-20">
              <EmptyState
                icon={ShoppingBag}
                title="No Purchases Found"
                description="There are no purchases matching your current filters. Try adjusting your search criteria."
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
                    <tr>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Purchase ID</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">User</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Policy & Route</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Amount</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Transaction ID</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Date</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Status</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <AnimatePresence>
                      {paginatedPurchases.map((purchase, index) => {
                        const user = purchase.user || {};
                        const policy = purchase.policy || {};
                        const purchasedData = purchase.purchased_policy_data || {};
                        const userName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "N/A";
                        
                        // Calculate amount based on policy type
                        let amount = 0;
                        if (policy.policy_type === "intercity_travel") {
                          // For intercity travel: use amount or coverageDuration.price
                          amount = parseFloat(
                            purchasedData.amount || 
                            purchasedData.coverageDuration?.price || 
                            purchase.amount || 
                            0
                          );
                        } else {
                          // For other policy types: use premium or amount
                          amount = parseFloat(
                            purchasedData.premium || 
                            purchasedData.amount || 
                            purchase.amount || 
                            0
                          );
                        }
                        
                        const paymentStatus = purchase.payment_status;
                        const purchaseDateTime = formatPurchaseDateTime(purchase.purchase_date);

                        return (
                          <motion.tr
                            key={purchase.slug || purchase.id || index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="transition-colors hover:bg-blue-50/50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="p-1 bg-blue-100 rounded">
                                    <FileText className="w-3 h-3 text-blue-600" />
                                  </div>
                                  <span className="font-mono text-xs font-semibold text-blue-700">
                                    {purchase.slug?.slice(0, 8).toUpperCase() || "N/A"}
                                  </span>
                                </div>
                              </motion.div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                <div className="flex items-center">
                                  <p className="font-semibold text-gray-900 truncate">{userName}</p>
                                </div>
                                <p className="text-xs text-gray-500 truncate">{user.email || "N/A"}</p>
                                {purchase.phone_number && (
                                  <p className="text-xs text-gray-400 truncate">{purchase.phone_number} ({purchase.network})</p>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                <p className="font-semibold text-gray-900 truncate" title={policy.policy_name}>
                                  {policy.policy_name || "N/A"}
                                </p>
                                <p className="text-xs text-gray-500 capitalize truncate">
                                  {policy.policy_type?.replace(/_/g, " ") || "N/A"}
                                </p>
                                
                                {/* Intercity Travel - Show route details */}
                                {policy.policy_type === "intercity_travel" && purchasedData.coverageDuration && (
                                  <div className="mt-1 space-y-0.5">
                                    <p className="text-xs text-gray-600 truncate">
                                      <span className="font-medium">{purchasedData.departureCity || purchasedData.departureRegion || "N/A"}</span>
                                      {" → "}
                                      <span className="font-medium">{purchasedData.destinationCity || purchasedData.destinationRegion || "N/A"}</span>
                                    </p>
                                    {purchasedData.busLicensePlate && (
                                      <p className="text-xs text-gray-500 truncate">
                                        {purchasedData.vehicleType || "Vehicle"} - {purchasedData.busLicensePlate}
                                      </p>
                                    )}
                                    <p className="text-xs text-emerald-600 font-medium truncate">
                                      {purchasedData.coverageDuration.display_value} {purchasedData.coverageDuration.display_unit} • ₵{purchasedData.coverageDuration.price || purchasedData.amount}
                                    </p>
                                    {purchasedData.coverageDuration.route_destination && (
                                      <p className="text-xs text-gray-400 truncate">
                                        Route: {purchasedData.coverageDuration.route_destination}
                                      </p>
                                    )}
                                  </div>
                                )}

                                {/* Other Policy Types - Show package details */}
                                {policy.policy_type !== "intercity_travel" && purchasedData.product_name && (
                                  <div className="mt-1 space-y-0.5">
                                    <p className="text-xs font-semibold text-blue-600 truncate">
                                      {purchasedData.product_name}
                                    </p>
                                    <p className="text-xs text-gray-600 truncate">
                                      <span className="font-medium">Premium:</span> ₵{purchasedData.premium}
                                      {purchasedData.premium_duration && (
                                        <span className="text-gray-500"> / {purchasedData.premium_duration}</span>
                                      )}
                                    </p>
                                    {purchasedData.duration && (
                                      <p className="text-xs text-gray-500 truncate">
                                        <span className="font-medium">Duration:</span> {purchasedData.duration}
                                      </p>
                                    )}
                                    {purchasedData.description && (
                                      <p className="text-xs text-gray-400 truncate whitespace-nowrap max-w-xs" title={purchasedData.description}>
                                        {purchasedData.description}
                                      </p>
                                    )}
                                    {purchasedData.insurance_coverage_list && purchasedData.insurance_coverage_list.length > 0 && (
                                      <p className="text-xs text-emerald-600 truncate">
                                        {purchasedData.insurance_coverage_list.length} coverage{purchasedData.insurance_coverage_list.length > 1 ? 's' : ''}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap">
                              ₵{amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {purchase.transaction_id ? (
                                <div className="text-sm">
                                  <p className="font-mono text-xs text-gray-700 truncate" title={purchase.transaction_id}>
                                    {purchase.transaction_id}
                                  </p>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400 italic">N/A</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {purchaseDateTime ? (
                                <div className="text-sm">
                                  <p className="font-medium text-gray-900">{purchaseDateTime.date}</p>
                                  <p className="text-xs text-gray-500">{purchaseDateTime.time}</p>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400 italic">N/A</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge paymentStatus={paymentStatus} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                  // Modal functionality can be added later
                                  toast.info("Purchase details view coming soon");
                                }}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100 border border-blue-200"
                              >
                                <Eye className="mr-1 w-4 h-4" />
                                View
                              </motion.button>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-blue-200">
                <div className="flex flex-col items-center justify-between lg:flex-row">
                  <div className="flex items-center mb-4 space-x-2 lg:mb-0">
                    <span className="text-sm text-gray-700">
                      Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredPurchases.length)} of {filteredPurchases.length} purchases
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <span className="px-4 py-2 text-sm text-gray-700 font-medium">
                      Page {currentPage} of {totalPages || 1}
                    </span>

                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="p-2 rounded border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage >= totalPages}
                      className="p-2 rounded border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
